import React, { useEffect } from 'react';
import {
  Box,
  Card,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CommonLayout from 'layouts/DashboardLayout';
import AngleRightIcon from 'common/customIcons/angle_right';
import MDButton from 'components/MDButton';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { fetchSingleDistributor } from '../../redux/slices/distributor';
import StatusText from 'common/component/StatusText';

const BASE_PATH = '/hyperwolf/distributors';

export default function DistributorDetails() {
  const { distributorId = '' } = useParams();
  const [searchParams] = useSearchParams();
  const blazeVendorId = searchParams.get('blazeVendorId') || undefined;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { distributorDetails = {}, isFetching = false } = useSelector(
    (state) => state.distributor
  );

  useEffect(() => {
    if (distributorId) {
      dispatch(fetchSingleDistributor({ distributorId, blazeVendorId }));
    }
  }, [dispatch, distributorId, blazeVendorId]);

  const {
    distributorName,
    distributorLicenseNumber,
    contactPersonName,
    distributorBillingEmail,
    phoneNumber,
    status,
  } = distributorDetails || {};

  const addressObj =
    distributorDetails?.address && typeof distributorDetails.address === 'object'
      ? distributorDetails.address
      : {};

  const formatValue = (value, emptyFallback = "-") => {
    if (value === null || value === undefined) return emptyFallback;
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      if (!normalized || normalized === "-" || normalized === "--" || normalized === "null" || normalized === "undefined") {
        return emptyFallback;
      }
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        return value.map((v) => formatValue(v, emptyFallback)).filter((v) => v !== emptyFallback).join(", ") || emptyFallback;
      }
      const parts = [
        value.address,
        value.addressLine2,
        value.city,
        value.state,
        value.zipCode || value.pincode || value.postalCode,
        value.country,
      ].filter(Boolean);
      if (parts.length) return parts.join(", ");
      try {
        return JSON.stringify(value);
      } catch {
        return emptyFallback;
      }
    }
    return String(value);
  };

  const addressLine = formatValue(addressObj.address || addressObj.addressLine1 || "");
  const city = formatValue(addressObj.city || distributorDetails.city || "");
  const state = formatValue(addressObj.state || distributorDetails.state || "");
  const postalCode = formatValue(
    addressObj.zipCode || addressObj.pincode || addressObj.postalCode || distributorDetails.postalCode || ""
  );

  const normalizedStatus =
    status === true || status === 'active'
      ? 'active'
      : status === false || status === 'inactive'
      ? 'inactive'
      : '';

  const formattedPhone = phoneNumber ? `+1 ${phoneNumber}` : '-';

  return (
    <CommonLayout>
      <Box p={{ xs: 2, sm: 3, lg: 4 }} pb={{ lg: 2 }}>
        <Box display="flex" alignItems="center" color="text.main" lineHeight={1} mb={2}>
          <IconButton onClick={() => navigate(BASE_PATH)}>
            <AngleRightIcon fontSize="14px" />
          </IconButton>
          <Typography variant="h6" fontWeight="semibold" ml={0.75}>
            Distributors
          </Typography>
        </Box>

        <Card sx={{ p: { xs: 2.5, md: 3 }, borderRadius: 2.5, boxShadow: 'none' }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            mb={3}
            gap={2}
          >
            <Typography variant="h5" fontWeight="bold">
              Distributor Information
            </Typography>

            <Stack direction="row" alignItems="center" gap={2}>
              {normalizedStatus && (
                <StatusText status={normalizedStatus} />
              )}
              {distributorId && (
                <MDButton
                  type="button"
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                  const q = blazeVendorId ? `?blazeVendorId=${encodeURIComponent(blazeVendorId)}` : (distributorDetails?.blazeVendorId ? `?blazeVendorId=${encodeURIComponent(distributorDetails.blazeVendorId)}` : '');
                  navigate(`${BASE_PATH}/${distributorId}/edit${q}`);
                }}
                >
                  Edit
                </MDButton>
              )}
            </Stack>
          </Stack>

          <Grid container spacing={3} mb={3}>
            <Grid item xs={12} md={4}>
              <Typography variant="caption" color="text.secondary">
                Distributor Name*
              </Typography>
              <Typography variant="body1" fontWeight="small">
                {formatValue(distributorName)}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="caption" color="text.secondary">
                Distributor License Number*
              </Typography>
              <Typography variant="body1" fontWeight="small">
                {formatValue(distributorLicenseNumber)}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="caption" color="text.secondary">
                Phone Number
              </Typography>
              <Typography variant="body1" fontWeight="small">
                {formattedPhone}
              </Typography>
            </Grid>
          </Grid>

          <Grid container spacing={3} mb={3}>
            <Grid item xs={12} md={4}>
              <Typography variant="caption" color="text.secondary">
                Contact Name
              </Typography>
              <Typography variant="body1" fontWeight="small">
                {formatValue(contactPersonName)}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="caption" color="text.secondary">
                Email
              </Typography>
              <Typography variant="body1" fontWeight="small">
                {formatValue(distributorBillingEmail)}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="subtitle1" fontWeight="semibold" mb={1.5}>
            Address
          </Typography>
          <Box>
            <Typography variant="body2" mb={0.5}>
              <strong>Address:</strong> {formatValue(addressLine, "N/A")}
            </Typography>
            <Typography variant="body2" mb={0.5}>
              <strong>City:</strong> {formatValue(city, "N/A")}
            </Typography>
            <Typography variant="body2" mb={0.5}>
              <strong>State:</strong> {formatValue(state, "N/A")}
            </Typography>
            <Typography variant="body2">
              <strong>Zip Code:</strong> {formatValue(postalCode, "N/A")}
            </Typography>
          </Box>

          {isFetching && (
            <Typography variant="caption" color="text.secondary" mt={2} display="block">
              Loading distributor details...
            </Typography>
          )}
        </Card>
      </Box>
    </CommonLayout>
  );
}

